import { ID, LineItem, Lot } from 'types';
import { fetchSmartBaselineFlavorsByItemName } from '@api/SmartBaselineFlavorsApi';
import { getCachedFlavors } from './nsbFlavorsCache';
import { getResolvedItemNameToUse, getResolvedNameForLineItem } from './nsbResolvedItemBridge';
import { isHeldForIco } from './nsbIcoFallbackStore';
import { recordMirroredBaseline, seedFromFlavor } from './nsbPriceSeed';
import { getNsbRowConfig, getNsbRowSourcesForSave } from './nsbRowConfigStore';
import { isUserProvided } from './userProvidedStore';
import {
  INTELLIGENT_COUNTER_OFFER,
  MANUAL_ENTRY,
  MIRROR_STARTING_PRICE,
  getBaselineSelection,
  getStartingPriceSelection,
} from './nsbSelectionStore';

export interface NsbBackfilledPrices {
  negotiation_starting_price?: number;
  price?: number;
}

export type NsbBackfill = Record<string, Record<string, NsbBackfilledPrices>>;

const cacheKeyFor = (itemNameToUse: string): string => `byname__${itemNameToUse}`;

/**
 * A row's resolved catalogue name: by NAME first, by id second.
 *
 * The sweep records both, but the id-keyed entries are filed against whatever id a row had at the time,
 * and an import's save-and-reload replaces every `new-<uuid>` with a real one - orphaning them. The
 * name-keyed entry survives that, which is why the cells and the match stats stay correct on the first
 * save while an id-keyed reader sees an event full of unmatched rows.
 */
const resolutionFor = (name: string | null | undefined, lineItemId: string): string | undefined =>
  getResolvedItemNameToUse(name ?? '') || getResolvedNameForLineItem(lineItemId);

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '' || Number(value) === 0;

// A selection that names an actual flavour ('recommended' or F1..F8), as opposed to one of the three
// methods that mean "do not derive a smart price for this column".
const isFlavorSelection = (selection: string): boolean =>
  Boolean(selection)
  && selection !== MANUAL_ENTRY
  && selection !== MIRROR_STARTING_PRICE
  && selection !== INTELLIGENT_COUNTER_OFFER;

/**
 * The baseline column's price for one row.
 *
 * Mirror is not a flavour: it means this column shows the negotiation price, so it takes that column's
 * value on this row rather than looking anything up.
 *
 * ⚠️ The mirror source is the derived price OR the row's EXISTING one. A negotiation price that arrived
 * in the import is not blank, so nothing is derived for it and the derived value is null -- but the cell
 * on screen mirrors the imported value regardless, so without this fallback the buyer sees a baseline and
 * the database gets a blank. Same divergence this module exists to close, reached through the import.
 */
const priceForBaselineFactory = (baselineSelection: string, mirrorBaseline: boolean) =>
  (
    precomputes: Parameters<typeof seedFromFlavor>[1],
    mirrorSource: number | null,
    sbKey: string,
    lotId: ID,
  ): number | null => {
    if (mirrorBaseline) {
      if (mirrorSource != null) recordMirroredBaseline(sbKey, lotId);
      return mirrorSource;
    }
    return seedFromFlavor('baseline', precomputes, baselineSelection, sbKey, lotId).price;
  };

/**
 * Smart Baseline prices for rows that were never drawn.
 *
 * A price reaches the database only if a cell wrote it, and cells exist only for rendered rows — so on a
 * paginated event the saved baselines are whichever rows the buyer happened to scroll past. Bidding and
 * savings read the ordinary `price` / `negotiation_starting_price` columns, so the rest go out with
 * nothing. This closes that gap at save time, which is the last moment the buyer's choices are final:
 * anything computed at apply could be overridden afterwards.
 *
 * Deliberately conservative. A row is filled only when it has a resolved match, the column is still
 * empty, and nobody has decided anything about it — a user-provided baseline, a row-level flavour pick,
 * or a Manual Entry override all mean hands off. Filling a gap must never overwrite a decision.
 *
 * Returns lotId -> lineItemId -> the columns to write, ready to merge into the save's pending changes.
 * Never rejects: a failed fetch just leaves those rows as they are today.
 *
 * The per-name fetch below looks like a fan-out but is batched by flushFlavorJobs (50ms window), so
 * hundreds of missing names land as a handful of POSTs. ⚠️ Do NOT swap it for the full loader
 * (fetchSmartBaselineFlavorsFull) -- that one is deliberately unbatched and this becomes a burst.
 */
export const buildMissingNsbPriceChanges = async (lots: Lot[], eventId: ID): Promise<NsbBackfill> => {
  const startingSelection = getStartingPriceSelection();
  const baselineSelection = getBaselineSelection();
  const fillStarting = isFlavorSelection(startingSelection);
  const mirrorBaseline = baselineSelection === MIRROR_STARTING_PRICE;
  const fillBaseline = isFlavorSelection(baselineSelection) || mirrorBaseline;
  if (!fillStarting && !fillBaseline) return {};

  interface Candidate {
    baselineBlank: boolean;
    // The negotiation price already on the row. Mirror falls back to it when nothing was derived.
    existingNsp: number | null;
    lineItemId: string;
    lotId: ID;
    resolvedName: string;
    sbKey: string;
    startingBlank: boolean;
  }

  const priceForBaseline = priceForBaselineFactory(baselineSelection, mirrorBaseline);

  const candidates: Candidate[] = [];
  lots.forEach(lot => {
    (lot.line_items ?? []).forEach((li: LineItem) => {
      const lineItemId = String(li.id);
      const resolvedName = resolutionFor(li.name, lineItemId);
      if (!resolvedName) return;
      const sbKey = `${eventId}_${lineItemId}`;
      if (isUserProvided(sbKey)) return;
      const config = getNsbRowConfig(sbKey);
      const startingBlank = fillStarting
        && !config?.negotiation
        && !isHeldForIco(getNsbRowSourcesForSave(sbKey)?.negotiation, sbKey)
        && isBlank(li.negotiation_starting_price);
      const baselineBlank = fillBaseline && !config?.baseline && isBlank(li.price);
      if (!startingBlank && !baselineBlank) return;
      candidates.push({
        baselineBlank,
        existingNsp: Number(li.negotiation_starting_price) || null,
        lineItemId,
        lotId: lot.id,
        resolvedName,
        sbKey,
        startingBlank,
      });
    });
  });
  if (candidates.length === 0) return {};

  const names = Array.from(new Set(candidates.map(c => c.resolvedName)));
  const flavorsByName = new Map<string, ReturnType<typeof getCachedFlavors>>();
  await Promise.all(names.map(async name => {
    const cached = getCachedFlavors(cacheKeyFor(name));
    if (cached?.precomputes) {
      flavorsByName.set(name, cached);
      return;
    }
    try {
      const fetched = await fetchSmartBaselineFlavorsByItemName(name);
      if (fetched?.precomputes) flavorsByName.set(name, fetched);
    } catch {
      flavorsByName.delete(name);
    }
  }));

  const backfill: NsbBackfill = {};
  candidates.forEach(({ baselineBlank, existingNsp, lineItemId, lotId, resolvedName, sbKey, startingBlank }) => {
    const precomputes = flavorsByName.get(resolvedName)?.precomputes;
    if (!precomputes) return;

    const startingPrice = startingBlank
      ? seedFromFlavor('negotiation', precomputes, startingSelection, sbKey, lotId).price
      : null;
    const baselinePrice = baselineBlank
      ? priceForBaseline(precomputes, startingPrice ?? existingNsp, sbKey, lotId)
      : null;

    const columns: NsbBackfilledPrices = {};
    if (startingPrice !== null && startingPrice !== undefined) columns.negotiation_starting_price = startingPrice;
    if (baselinePrice !== null && baselinePrice !== undefined) columns.price = baselinePrice;
    if (Object.keys(columns).length === 0) return;

    const lotKey = String(lotId);
    if (!backfill[lotKey]) backfill[lotKey] = {};
    backfill[lotKey][lineItemId] = columns;
  });
  return backfill;
};
